import React, { useEffect, useMemo, useState } from "react";
import AdminLayout from "@/components/AdminLayout";
import { http } from "@/lib/api";
import { ChevronLeft, ChevronRight } from "lucide-react";

const MONTHS = ["Gennaio","Febbraio","Marzo","Aprile","Maggio","Giugno","Luglio","Agosto","Settembre","Ottobre","Novembre","Dicembre"];
const DAYS = ["L","M","M","G","V","S","D"];

function daysInMonth(y, m) { return new Date(y, m + 1, 0).getDate(); }
function firstDayIdx(y, m) { return (new Date(y, m, 1).getDay() + 6) % 7; }
function fmt(d) { return d.toISOString().slice(0, 10); }

export default function AdminCalendar() {
  const [properties, setProperties] = useState([]);
  const [propId, setPropId] = useState("");
  const [bookings, setBookings] = useState([]);
  const [cursor, setCursor] = useState(new Date());

  useEffect(() => {
    http.get("/properties?active_only=false").then((r) => {
      setProperties(r.data);
      if (r.data.length && !propId) setPropId(r.data[0].property_id);
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- runs once on mount

  useEffect(() => {
    if (!propId) return;
    http.get(`/calendar/${propId}`).then((r) => setBookings(r.data));
  }, [propId]);

  const y = cursor.getFullYear();
  const m = cursor.getMonth();
  const total = daysInMonth(y, m);
  const first = firstDayIdx(y, m);

  const statusByDay = useMemo(() => {
    const map = {};
    bookings.forEach((b) => {
      const ci = new Date(b.check_in);
      const co = new Date(b.check_out);
      for (let d = new Date(ci); d < co; d.setDate(d.getDate() + 1)) {
        map[fmt(d)] = b.status;
      }
    });
    return map;
  }, [bookings]);

  const cells = [];
  for (let i = 0; i < first; i++) cells.push(null);
  for (let d = 1; d <= total; d++) cells.push(new Date(y, m, d));

  const shift = (delta) => setCursor(new Date(y, m + delta, 1));

  return (
    <AdminLayout>
      <div className="p-10">
        <div className="eyebrow mb-2">Vista</div>
        <h1 className="font-serif text-4xl mb-8">Calendario</h1>

        <div className="flex flex-wrap items-center gap-4 mb-8">
          <select value={propId} onChange={(e) => setPropId(e.target.value)} className="border border-[var(--mel-border)] rounded-md px-3 py-2 text-sm bg-white" data-testid="cal-property-select">
            {properties.map((p) => <option key={p.property_id} value={p.property_id}>{p.name}</option>)}
          </select>
          <div className="flex items-center gap-2">
            <button onClick={() => shift(-1)} className="p-2 border rounded-md" data-testid="cal-prev"><ChevronLeft size={16} /></button>
            <div className="font-serif text-xl min-w-[200px] text-center">{MONTHS[m]} {y}</div>
            <button onClick={() => shift(1)} className="p-2 border rounded-md" data-testid="cal-next"><ChevronRight size={16} /></button>
          </div>
          <div className="flex items-center gap-4 text-xs ml-auto">
            <span className="flex items-center gap-2"><span className="w-3 h-3 rounded-full bg-[var(--mel-accent)]"></span> Confermato</span>
            <span className="flex items-center gap-2"><span className="w-3 h-3 rounded-full bg-[var(--mel-primary)]"></span> In attesa</span>
          </div>
        </div>

        <div className="border border-[var(--mel-border)] rounded-md overflow-hidden bg-white" data-testid="calendar-grid">
          <div className="grid grid-cols-7 border-b border-[var(--mel-border)]">
            {DAYS.map((d, i) => <div key={`${d}-${i}`} className="text-center py-3 text-xs uppercase tracking-widest text-[var(--mel-muted)]">{d}</div>)}
          </div>
          <div className="grid grid-cols-7">
            {cells.map((c, i) => {
              if (!c) return <div key={`pad-${i}`} className="h-24 border-b border-r border-[var(--mel-border)] bg-[var(--mel-surface)]/40" />;
              const key = fmt(c);
              const status = statusByDay[key];
              const bg = status === "confirmed" ? "bg-[var(--mel-accent)] text-white" : status === "pending" ? "bg-[var(--mel-primary)] text-white" : "";
              return (
                <div key={key} className="h-24 border-b border-r border-[var(--mel-border)] p-2 relative" data-testid={`cal-day-${key}`}>
                  <div className={`inline-block px-2 py-0.5 rounded ${bg}`}>{c.getDate()}</div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
